import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import style from './WmsCheckBox.scss';

function WmsCheckBox({
	id,
	label,
	name,
	wmsSelector,
	onChangeFunc,
	checked = false,
	disabled = false,
	parentClassName = "",
	isRounded = false,
	showToolTip = false,
	showLabelInfoIcon = false,
	labelInfoTooltipText = "",
	isSwitchCheckbox = false,
	tooltipShowAfter = 800,
	tooltipText = 'add tooltip text',
}) {
	const props = {
		id,
		label,
		name,
		wmsSelector,
		onChangeFunc,
		checked,
		disabled,
		parentClassName,
		isRounded,
		showToolTip,
		showLabelInfoIcon,
		labelInfoTooltipText,
		isSwitchCheckbox,
		tooltipShowAfter,
		tooltipText,
	};
	const [isHovering, setIsHovering] = useState(false);
	const inputRef = useRef(null);
	const labelInfoRef = useRef(null);
	const propsRef = useRef(props);
	propsRef.current = props;

	// Native listeners: React synthetic events can miss after Kendo Window reparents the DOM.
	useEffect(() => {
		const inputElement = inputRef.current;
		if (!inputElement) {
			return undefined;
		}

		const handleNativeChange = (event) => {
			if (propsRef.current.disabled) {
				return;
			}
			const nextChecked = !Boolean(propsRef.current.checked);
			propsRef.current.onChangeFunc({
				target: {
					checked: nextChecked,
					name: propsRef.current.name,
					id: propsRef.current.id,
					type: "checkbox",
					value: event.target.value,
				},
				currentTarget: event.currentTarget,
				preventDefault: () => event.preventDefault(),
				stopPropagation: () => event.stopPropagation(),
				nativeEvent: event,
			});
		};

		inputElement.addEventListener('change', handleNativeChange);
		return () => {
			inputElement.removeEventListener('change', handleNativeChange);
		};
	}, []);

	// Same hover bubble as WmsDropdown's packing-slip layout icon. Not the label-text tooltip.
	useEffect(() => {
		if (!showLabelInfoIcon) {
			return undefined;
		}
		let icon = null;
		const timer = setTimeout(function () {
			icon = labelInfoRef.current;
			if (!icon || !window.$ || typeof window.$.fn.kendoTooltip !== "function") {
				return;
			}
			const $labelInfo = window.$(icon);
			if (typeof $labelInfo.data("kendoTooltip") == "undefined") {
				$labelInfo.kendoTooltip({
					width: 240,
					position: "bottom",
					content: function () {
						return '<p class="model-tooltip-title-p">' + (propsRef.current.labelInfoTooltipText || "") + '</p>';
					},
					animation: {
						open: {
							effects: "zoom",
							duration: 150
						}
					}
				});
			}
		}, 100);
		return function () {
			clearTimeout(timer);
			if (!icon || !window.$) {
				return;
			}
			const tooltip = window.$(icon).data("kendoTooltip");
			if (tooltip) {
				tooltip.destroy();
			}
		};
	}, [showLabelInfoIcon]);

	const handleMouseHover = () => {
		const chkElement = document.querySelector(`#${props.id}_chk`).parentElement.getBoundingClientRect();
		const tooltipElement = document.querySelector(`#${props.id}_chk .tooltip-wrapper`);
		var bottom = chkElement.bottom;
		var left = chkElement.left;
		if (tooltipElement.closest('.wms-model-new-ui-wrapper')) {
			var modalPosition = tooltipElement.closest('.wms-model-new-ui-wrapper').getBoundingClientRect();
			bottom = bottom - (modalPosition && modalPosition.top ? modalPosition.top : 100);
			left = left - (modalPosition && modalPosition.left ? (modalPosition.left + 50)  : 192.5);
		}
		tooltipElement.style.top = `${bottom}px`;
		tooltipElement.style.left = `${left}px`;

		tooltipElement.style.transitionDelay = `${props.tooltipShowAfter}ms`;
		setIsHovering(true);
	}

	const handleMouseLeave = () => {
		setIsHovering(false);
	}
	return (
		<div className={props.parentClassName + " wms-checkbox-component " + (props.isRounded ? 'wms-rounded-checkbox' : 'wms-rounded-checkbox')
			+ (props.isSwitchCheckbox ? ' wms-toggle-checkbox' : '') +  (props.disabled ? ' disabled-chk' : '')} >
			<label id={props.id + '_chk'} htmlFor={props.id}>
				<input
					ref={inputRef}
					type="checkbox"
					id={props.id}
					name={props.name}
					data-wms-selector={props.wmsSelector}
					checked={Boolean(props.checked)}
					onChange={() => {}}
					className={(props.isSwitchCheckbox ? "switch" : "option-input")}
					disabled={props.disabled}
				/> <span
					onMouseEnter={props.showToolTip == true ? handleMouseHover : undefined}
					onMouseLeave={props.showToolTip == true ? handleMouseLeave : undefined}>{props.label}</span>
				<div className="tooltip-wrapper">
					{props.showToolTip == true && isHovering == true && (
						<div dangerouslySetInnerHTML={{ __html: props.tooltipText }}></div>
					)}
				</div>
			</label>
			{props.showLabelInfoIcon && (
				<span
					className="fa fa-info-circle wms-checkbox-info"
					id={props.id + '_labelinfo'}
					ref={labelInfoRef}
					aria-hidden="true"
					onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
				/>
			)}
		</div>
	);
}

WmsCheckBox.propTypes = {
	id: PropTypes.string, // this should write the id to the checkbox
	label: PropTypes.string.isRequired,// this should be the label that shows after checkbox button
	name: PropTypes.string.isRequired,// this should be the name of the checkbox
	wmsSelector: PropTypes.string.isRequired, // this should set up a selector on the input
	onChangeFunc: PropTypes.func.isRequired, // this should control the onchange event and anything we need to determine before we send it up
	parentClassName: PropTypes.string, //    this should parent tag class
	checked: PropTypes.bool, // this should disable checkbox
	isRounded: PropTypes.bool, // this is use for rounded and square checkbox shown
	showToolTip: PropTypes.bool, // set tooltip show or not
	showLabelInfoIcon: PropTypes.bool,
	labelInfoTooltipText: PropTypes.string,
	isSwitchCheckbox: PropTypes.bool, // set tooltip show or not
	tooltipText: PropTypes.string // show text on hover as tooltip
}

const areEqual = (prevProps, nextProps) => {
	let isChangeFuncEqual = _.isEqual(_.omit(nextProps, ['onChangeFunc']), _.omit(prevProps, ['onChangeFunc']));
	return isChangeFuncEqual;
};


export default React.memo(WmsCheckBox, areEqual);
